import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import CashDrillDrawer from "@/components/CashDrillDrawer";
import { api, formatEUR, formatEURCompact } from "@/lib/api";
import {
  TrendUp, Target, CheckCircle, Warning, WarningCircle, Lightning,
  Calendar, Flame, Funnel, Bank, ShieldCheck, ChartLineUp, Clock,
  CurrencyEur, Wallet, Receipt, ListNumbers, PencilSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/* ---------- Atoms ---------- */

function ProgressBar({ pct, tone = "blue", h = 8 }) {
  const clamped = Math.min(100, Math.max(0, pct || 0));
  const color =
    tone === "green" ? "var(--cf-success)" :
    tone === "warning" ? "var(--cf-warning)" :
    tone === "danger" ? "var(--cf-danger)" : "var(--cf-primary)";
  return (
    <div className="w-full bg-slate-100 rounded-full overflow-hidden" style={{ height: h }}>
      <div className="h-full rounded-full transition-all" style={{ width: `${clamped}%`, background: color }} />
    </div>
  );
}

function StatusBanner({ status }) {
  if (!status) return null;
  const map = {
    success: { bg: "var(--cf-success-soft)", border: "var(--cf-success)", color: "var(--cf-success-text)", icon: CheckCircle },
    warning: { bg: "var(--cf-warning-soft)", border: "var(--cf-warning)", color: "var(--cf-warning-text)", icon: WarningCircle },
    danger:  { bg: "var(--cf-danger-soft)", border: "var(--cf-danger)", color: "var(--cf-danger-text)", icon: Warning },
  };
  const p = map[status.tone] || map.warning;
  const Icon = p.icon;
  return (
    <div
      className="rounded-xl px-4 py-3 flex items-center gap-3 mb-5 border"
      style={{ background: p.bg, borderColor: p.border }}
      data-testid="cash-status-banner"
    >
      <Icon size={22} color={p.color} weight="fill" />
      <div className="font-bold text-[15px]" style={{ color: p.color }}>{status.label}</div>
    </div>
  );
}

function KpiBigCard({ label, value, sub, color, icon: Icon, testid }) {
  return (
    <div className="card-base p-4 md:p-5" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <span className="overline">{label}</span>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `color-mix(in srgb, ${color} 14%, white)` }}>
          <Icon size={18} color={color} weight="fill" />
        </div>
      </div>
      <div className="metric-value text-3xl md:text-4xl" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1 font-medium">{sub}</div>}
    </div>
  );
}

function SectionTitle({ icon: Icon, color, title, subtitle, right }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && (
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: (color || "var(--cf-primary)") + "1A" }}>
            <Icon size={18} color={color || "var(--cf-primary)"} weight="fill" />
          </div>
        )}
        <div>
          <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900 leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

function MiniMetric({ label, value, color = "var(--cf-text)", testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">{label}</div>
      <div className="font-extrabold text-[18px] md:text-[20px] tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}

function EditableMiniMetric({ label, value, onSave, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  useEffect(() => setVal(value), [value]);
  const save = async () => {
    setEditing(false);
    if (Number(val) !== Number(value)) await onSave(Number(val) || 0);
  };
  return (
    <div data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
        {label}
        <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-slate-600" data-testid={`${testid}-edit`}>
          <PencilSimple size={11} />
        </button>
      </div>
      {editing ? (
        <input
          autoFocus
          type="number"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => e.key === "Enter" && save()}
          className="w-28 border border-slate-300 rounded px-2 py-1 text-[18px] font-extrabold"
          data-testid={`${testid}-input`}
        />
      ) : (
        <div className="font-extrabold text-[18px] md:text-[20px] tabular-nums text-slate-900">
          {formatEUR(value)}
        </div>
      )}
    </div>
  );
}

/* ---------- Page ---------- */

export default function DashboardCash() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [goals, setGoals] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [drillMonth, setDrillMonth] = useState(null);
  // V4.48 — vue canonique active (Brut / Pondéré / Net) + snapshot pour le prévisionnel
  const [cashVue, setCashVue] = useState("net");
  const [previsionnel, setPrevisionnel] = useState(null);

  const load = async (y) => {
    setLoading(true);
    try {
      const [cashRes, goalsRes, snapRes, ratesRes] = await Promise.all([
        api.get(`/dashboard/cash${y ? `?year=${y}` : ""}`),
        api.get("/goals"),
        api.get("/copilot/snapshot").catch(() => null),
        api.get("/stats/conversion-rates?window_months=12").catch(() => null),
      ]);
      setData(cashRes.data);
      setGoals(goalsRes.data);
      // Compose le prévisionnel à partir du snapshot + fiabilité issue des taux observés
      if (snapRes?.data) {
        const p = snapRes.data.pipeline || {};
        const vc = snapRes.data.visibility_cash || {};
        const rates = ratesRes?.data || null;
        const global = rates?.global || {};
        setPrevisionnel({
          ca_previsionnel: p.ca_previsionnel ?? p.ca_pondere ?? 0,
          months: (vc.months || []).map((m) => ({
            label: m.label,
            ca_previsionnel: m.ca_previsionnel ?? m.ca_pondere ?? 0,
          })),
          fiabilite: global.fiabilite || "faible",
          n_resolus: global.sample_size ?? 0,
        });
      } else {
        setPrevisionnel(null);
      }
    } catch { toast.error("Impossible de charger Cash"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(year); }, [year]);

  const saveGoal = async (key, value) => {
    try {
      await api.patch("/goals", { [key]: value });
      toast.success("Mis à jour");
      load(year);
    } catch { toast.error("Erreur sauvegarde"); }
  };

  if (loading || !data) {
    return (
      <Layout title="Machine commerciale" subtitle="Pilotage cash & objectifs">
        <div className="card-base h-96 animate-pulse" />
      </Layout>
    );
  }

  const fmt = (v) => formatEUR(v);
  const k = (v) => formatEURCompact(v);
  const oa = data.objectif_annuel;
  const om = data.objectif_mois;
  const phr = data.phrase_executive;
  const perf = data.performance_ytd;
  const rythme = data.rythme;
  const proj = data.projection_fin_annee;
  const sante = data.sante_cash;
  const sit = data.situation_cash;
  const net = data.ca_net_remuneration;
  const tun = data.tunnel;
  const histo = data.historique_total;
  const kpis = data.kpis;

  /* === 1. Header + sélecteur année === */
  return (
    <Layout title="Machine commerciale" subtitle="Pilotage cash & objectifs">
      <div className="hidden md:flex items-end justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 text-slate-500 font-semibold text-sm mb-1">
            <Target size={14} weight="fill" color="var(--cf-success)" /> Machine commerciale
          </div>
          <h1 className="font-display text-4xl font-extrabold text-slate-900">Pilotage cash · {year}</h1>
          <p className="text-slate-500 mt-1">Reste à produire ce mois pour tenir tes objectifs.</p>
        </div>
      </div>

      {/* Year tabs */}
      <div className="flex items-center gap-2 mb-4" data-testid="cash-year-tabs">
        {[year - 1, year, year + 1].map((y) => (
          <button
            key={y}
            onClick={() => setYear(y)}
            className={`px-3 py-1.5 rounded-full text-[12px] font-bold ${
              y === year ? "bg-[color:var(--cf-primary)] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
            data-testid={`cash-year-${y}`}
          >
            {y}
          </button>
        ))}
      </div>

      {/* === 2. Bandeau statut global === */}
      <StatusBanner status={data.status_global} />

      {/* === 2.5 Paramètres pilotage (éditables centralisés) === */}
      {goals && (
        <section
          className="rounded-xl bg-white border border-slate-200 p-4 mb-4"
          data-testid="cash-pilot-params"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[12px] font-extrabold uppercase tracking-widest text-slate-900">
              Paramètres pilotage
            </h3>
            <span className="text-[10px] text-slate-400">Utilisés partout (Cap du jour · Cash · Modes)</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <EditableMiniMetric
              label="Obj. annuel CA net"
              value={goals.annual_ca_net || 0}
              onSave={(v) => saveGoal("annual_ca_net", v)}
              testid="param-annual-ca"
            />
            <EditableMiniMetric
              label="Obj. mensuel CA net"
              value={goals.monthly_ca_net || 0}
              onSave={(v) => saveGoal("monthly_ca_net", v)}
              testid="param-monthly-ca"
            />
            <EditableMiniMetric
              label="Trésorerie courante"
              value={goals.current_treasury || 0}
              onSave={(v) => saveGoal("current_treasury", v)}
              testid="param-treasury"
            />
            <EditableMiniMetric
              label="Charges fixes mensuelles"
              value={goals.monthly_fixed_charges || 0}
              onSave={(v) => saveGoal("monthly_fixed_charges", v)}
              testid="param-charges"
            />
          </div>
        </section>
      )}

      {/* === 3. KPIs Contacts / Mandats / Signés === */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="cash-kpis">
        <KpiBigCard label="Contacts" value={kpis.contacts} sub="RDV à faire + RDV fait" color="var(--cf-primary)" icon={Funnel} testid="kpi-contacts" />
        <KpiBigCard label="Mandats" value={kpis.mandats} sub="Mandat signé client" color="var(--cf-accent)" icon={ShieldCheck} testid="kpi-mandats" />
        <KpiBigCard
          label="Signés"
          value={kpis.signes}
          sub={kpis.obj_signes_mois ? `obj: ${kpis.obj_signes_mois}/mois` : null}
          color="var(--cf-success)"
          icon={CheckCircle}
          testid="kpi-signes"
        />
      </div>

      {/* === Visibilité Cash V3 — M+1 / M+2 / M+3 (accord_risque + offre_editee + acte_authentique) === */}
      {data.visibility_short_term && data.visibility_short_term.months && (
        <section className="card-base p-5 mb-4" data-testid="cash-visibility-short">
          <SectionTitle
            icon={Calendar}
            color="var(--cf-text)"
            title="Visibilité 3 mois"
            subtitle="Accord risque + offre éditée + acte signé non encaissé"
          />
          <table className="w-full mt-3 text-[13px]" data-testid="cash-v3-table">
            <thead>
              <tr className="text-[10.5px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="text-left py-2 font-bold">Mois</th>
                <th className="text-right py-2 font-bold">CA prévu</th>
                <th className="text-right py-2 font-bold">Trésorerie nette</th>
              </tr>
            </thead>
            <tbody>
              {data.visibility_short_term.months.map((m) => (
                <tr
                  key={m.month_key}
                  onClick={() => m.count > 0 && setDrillMonth(m)}
                  className={`border-b border-slate-100 last:border-0 transition-colors ${
                    m.count > 0 ? "cursor-pointer hover:bg-slate-50" : ""
                  }`}
                  data-testid={`cash-v3-row-${m.month_label}`}
                >
                  <td className="py-2.5">
                    <div className="font-bold text-slate-900">{m.month_label}</div>
                    <div className="text-[10.5px] text-slate-400 font-semibold">
                      {new Date(m.year, m.month - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })}
                      {m.count > 0 && <span className="ml-1">· {m.count} dossier{m.count > 1 ? "s" : ""}</span>}
                    </div>
                  </td>
                  <td className="text-right tabular-nums font-bold text-slate-900">{k(m.ca)}</td>
                  <td
                    className="text-right tabular-nums font-extrabold"
                    style={{ color: m.tresorerie >= 0 ? "var(--cf-success-text)" : "var(--cf-danger-text)" }}
                  >
                    {k(m.tresorerie)}
                  </td>
                </tr>
              ))}
              <tr className="bg-slate-50">
                <td className="py-3 font-extrabold text-slate-900 uppercase text-[11px] tracking-wider">Total 3 mois</td>
                <td className="text-right tabular-nums font-extrabold text-slate-900" data-testid="cash-v3-total-ca">{k(data.visibility_short_term.total_ca)}</td>
                <td
                  className="text-right tabular-nums font-extrabold"
                  style={{ color: data.visibility_short_term.total_tresorerie >= 0 ? "var(--cf-success-text)" : "var(--cf-danger-text)" }}
                  data-testid="cash-v3-total-tresorerie"
                >
                  {k(data.visibility_short_term.total_tresorerie)}
                </td>
              </tr>
            </tbody>
          </table>
          {data.visibility_short_term.fixed_charges > 0 && (
            <div className="text-[10.5px] text-slate-400 font-semibold mt-2 text-right">
              Charges fixes mensuelles : {k(data.visibility_short_term.fixed_charges)}
            </div>
          )}
          {data.visibility_short_term.months.some((m) => m.count > 0) && (
            <div className="text-[10.5px] text-slate-400 font-semibold mt-2">
              Astuce : clique une ligne pour voir les dossiers contributeurs.
            </div>
          )}
        </section>
      )}

      {/* === 4. Objectif du mois === */}
      <section className="card-base p-5 mb-4" data-testid="cash-obj-mois">
        <SectionTitle
          icon={Calendar}
          color="var(--cf-success)"
          title={`Objectif ${data.year}-${String(data.now_month).padStart(2, "0")}`}
          subtitle="Cible mensuelle CA net"
          right={
            <span className={`px-3 py-1 rounded-full text-[12px] font-bold ${om.atteint ? "bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)]" : "bg-slate-100 text-slate-600"}`}>
              {om.progress_pct.toFixed(0)}%
            </span>
          }
        />
        <ProgressBar pct={om.progress_pct} tone={om.atteint ? "green" : "warning"} h={10} />
        <div className="grid grid-cols-3 gap-4 mt-4">
          <MiniMetric label="Réalisé" value={k(om.realise_ca)} color="var(--cf-success)" testid="om-realise" />
          <MiniMetric label="Reste à faire" value={om.atteint ? "Atteint !" : k(om.reste_ca)} color={om.atteint ? "var(--cf-success)" : "var(--cf-text)"} testid="om-reste" />
          <MiniMetric label="Obj. ajusté (retard)" value={k(om.objectif_ajuste_ca)} color="var(--cf-warning)" testid="om-ajuste" />
        </div>
      </section>

      {/* === 5. Objectif Annuel === */}
      <section className="card-base p-5 mb-4" data-testid="cash-obj-annuel">
        <SectionTitle
          icon={Target}
          color="var(--cf-warning)"
          title={`Objectif Annuel ${data.year}`}
          subtitle={`${oa.mois_restants} mois restants`}
          right={
            <span className={`px-3 py-1 rounded-full text-[12px] font-bold ${oa.annual_progress_pct >= 100 ? "bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)]" : "bg-[color:var(--cf-warning-soft)] text-[color:var(--cf-warning-text)]"}`}>
              {oa.annual_progress_pct.toFixed(0)}%
            </span>
          }
        />
        <ProgressBar pct={oa.annual_progress_pct} tone={oa.annual_progress_pct >= 100 ? "green" : "warning"} h={10} />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
          <MiniMetric label="Signés" value={`${oa.signed}/${oa.objectif_signed}`} color="var(--cf-success)" testid="oa-signed" />
          <MiniMetric label="Restants" value={`${oa.restants} dossiers`} testid="oa-restants" />
          <MiniMetric label="Écart vs attendu" value={oa.ecart_vs_attendu} color={oa.ecart_vs_attendu < 0 ? "var(--cf-danger)" : "var(--cf-success)"} testid="oa-ecart" />
          <MiniMetric label="Obj. mensuel ajusté" value={`${oa.obj_mensuel_ajuste} /mois`} color="var(--cf-warning)" testid="oa-obj-mensuel" />
        </div>
      </section>

      {/* === 6. Phrase exécutive automatique === */}
      <section
        className="rounded-2xl p-5 mb-4 text-white shadow-[0_8px_24px_rgba(45,110,255,0.25)]"
        style={{ background: "linear-gradient(135deg, var(--cf-primary) 0%, var(--cf-primary-hover) 100%)" }}
        data-testid="cash-phrase-executive"
      >
        <div className="flex items-center gap-2 mb-2">
          <Lightning size={18} weight="fill" color="var(--cf-warning-border)" />
          <span className="text-[11px] font-bold uppercase tracking-widest opacity-95">Cap du mois — auto-calculé</span>
        </div>
        {phr.is_atteint ? (
          <div className="text-[18px] font-extrabold">🎉 Objectif annuel atteint ! Sécurise les dossiers en cours.</div>
        ) : (
          <>
            <div className="text-[15px] md:text-[17px] font-extrabold leading-snug">
              {phr.phrase}
            </div>
            <div className="text-[13px] mt-2 opacity-95">
              Objectif du jour : <span className="font-extrabold">{phr.daily_contacts}</span> contacts · <span className="font-extrabold">{phr.daily_mandates}</span> mandats
            </div>
          </>
        )}
      </section>

      {/* === 7. Performance YTD === */}
      <section className="card-base p-5 mb-4" data-testid="cash-perf-ytd">
        <SectionTitle icon={ChartLineUp} color="var(--cf-success)" title={`Performance ${data.year}`} subtitle="Année en cours" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniMetric label="CA total facturé" value={k(perf.ca_total_facture)} color="var(--cf-success)" testid="perf-ca" />
          <MiniMetric label="CAPP moyen" value={k(perf.capp_moyen)} testid="perf-capp" />
          <MiniMetric label="Net moyen/dossier" value={k(perf.net_moyen_dossier)} color="var(--cf-primary)" testid="perf-net-moy" />
          <MiniMetric label="Dossiers signés" value={perf.dossiers_signes} testid="perf-count" />
        </div>
      </section>

      {/* === 8. Rythme === */}
      <section className="card-base p-5 mb-4" data-testid="cash-rythme">
        <SectionTitle
          icon={Lightning}
          color={rythme.is_en_retard ? "var(--cf-warning)" : "var(--cf-success)"}
          title="Rythme"
          subtitle="Production mensuelle de dossiers signés"
          right={
            <span className={`px-3 py-1 rounded-full text-[12px] font-bold ${rythme.is_en_retard ? "bg-[color:var(--cf-warning-soft)] text-[color:var(--cf-warning-text)]" : "bg-[color:var(--cf-success-soft)] text-[color:var(--cf-success-text)]"}`}>
              {rythme.ratio_pct.toFixed(0)}%
            </span>
          }
        />
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl p-4 bg-[color:var(--cf-warning-soft)]" data-testid="rythme-actuel">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--cf-warning-text)] mb-1">Rythme actuel</div>
            <div className="font-extrabold text-2xl text-[color:var(--cf-warning-text)] tabular-nums">{rythme.actuel.toFixed(1)} dossiers/mois</div>
          </div>
          <div className="rounded-xl p-4 bg-[color:var(--cf-success-soft)]" data-testid="rythme-attendu">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--cf-success-text)] mb-1">Rythme attendu</div>
            <div className="font-extrabold text-2xl text-[color:var(--cf-success-text)] tabular-nums">{rythme.attendu.toFixed(1)} dossiers/mois</div>
          </div>
        </div>
      </section>

      {/* === 9. Projection fin d'année === */}
      <section className="card-base p-5 mb-4" data-testid="cash-projection">
        <SectionTitle icon={TrendUp} color="var(--cf-accent)" title="Projection fin d'année" subtitle={`Extrapolation rythme actuel × 12`} />
        <div className="grid grid-cols-2 gap-4">
          <MiniMetric label="CA projeté (net)" value={k(proj.ca_net_projected)} color="var(--cf-accent)" testid="proj-ca" />
          <MiniMetric label="Écart projeté" value={`${proj.ecart_dossiers >= 0 ? "+" : ""}${proj.ecart_dossiers} dossiers`} color={proj.ecart_dossiers < 0 ? "var(--cf-danger)" : "var(--cf-success)"} testid="proj-ecart" />
        </div>
      </section>

      {/* === 10. Santé Cash === */}
      <section className="card-base p-5 mb-4" data-testid="cash-sante">
        <SectionTitle icon={Wallet} color="var(--cf-warning)" title="Santé Cash" subtitle="Trésorerie, point mort, couverture" />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <EditableMiniMetric label="Trésorerie courante" value={sante.tresorerie_courante} onSave={(v) => saveGoal("current_treasury", v)} testid="sante-treasury" />
          <MiniMetric label="Flux disponible" value={fmt(sante.flux_disponible)} color="var(--cf-success)" testid="sante-flux" />
          <MiniMetric label="Couverture" value={`${sante.couverture_mois.toFixed(1)} mois`} testid="sante-couv" />
          <MiniMetric label="Point mort" value={`${sante.point_mort_dos_mois.toFixed(1)} dos./mois`} color="var(--cf-danger)" testid="sante-pm" />
          <MiniMetric label="Revenu moy/mois" value={fmt(sante.revenu_moy_mois)} testid="sante-revenu" />
          <EditableMiniMetric label="Charges fixes" value={sante.charges_fixes} onSave={(v) => saveGoal("monthly_fixed_charges", v)} testid="sante-charges" />
        </div>
      </section>

      {/* === 11. Situation Cash — V4.48 : 3 vues canoniques (BRUT / PONDÉRÉ / NET) === */}
      <section className="card-base p-5 mb-4" data-testid="cash-situation">
        <SectionTitle
          icon={CurrencyEur}
          color="var(--cf-primary)"
          title="Situation Cash"
          subtitle="Stocks par étape — 3 vues canoniques (Brut · Pondéré · Net)"
        />

        {/* Toggle 3 vues canoniques V4.48 */}
        <div className="mb-3 inline-flex items-center rounded-full bg-white border border-slate-200 p-0.5" role="tablist" data-testid="cash-vue-toggle">
          {[
            { key: "brut",    label: "Brut",    tip: "Avant rétrocession Pretto (honoraires + com banque + assurance)" },
            { key: "pondere", label: "Pondéré", tip: "Après Pretto, avant commissions apporteur / parrain (part broker)" },
            { key: "net",     label: "Net",     tip: "Après Pretto + commissions apporteur / parrain (rémunération réelle)" },
          ].map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setCashVue(v.key)}
              title={v.tip}
              className={`px-3 py-1 rounded-full text-[11px] font-extrabold uppercase tracking-wider transition-colors ${
                cashVue === v.key ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`cash-vue-${v.key}-btn`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniMetric
            label={`CA Signé (mois) · ${sit.ca_signed_month_count}`}
            value={k(cashVue === "brut" ? sit.ca_signed_month_brut : cashVue === "pondere" ? (sit.ca_signed_month_pondere ?? sit.ca_signed_month) : sit.ca_signed_month)}
            color="var(--cf-success)"
            testid="sit-signed"
          />
          <MiniMetric
            label={`CA Sécurisé · ${sit.ca_secured_count}`}
            value={k(cashVue === "brut" ? sit.ca_secured_brut : cashVue === "pondere" ? (sit.ca_secured_pondere ?? sit.ca_secured) : sit.ca_secured)}
            color="var(--cf-accent)"
            testid="sit-secured"
          />
          <MiniMetric
            label={`CA En banque · ${sit.ca_in_bank_count}`}
            value={k(cashVue === "brut" ? sit.ca_in_bank_brut : cashVue === "pondere" ? (sit.ca_in_bank_pondere ?? sit.ca_in_bank) : sit.ca_in_bank)}
            color="var(--cf-warning)"
            testid="sit-bank"
          />
          <MiniMetric
            label={`Pipeline total · ${sit.ca_pipeline_total_count}`}
            value={k(cashVue === "brut" ? sit.ca_pipeline_total_brut : cashVue === "pondere" ? (sit.ca_pipeline_total_pondere ?? sit.ca_pipeline_total) : sit.ca_pipeline_total)}
            color="var(--cf-primary)"
            testid="sit-pipe"
          />
        </div>
      </section>

      {/* === 11.5 CA Prévisionnel — V4.48 : projection probabiliste dédiée === */}
      {previsionnel && (
        <section
          className="rounded-2xl p-5 mb-4 border border-violet-200 bg-gradient-to-br from-violet-50 to-white"
          data-testid="cash-previsionnel"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-100">
                <TrendUp size={18} color="var(--cf-accent)" weight="fill" />
              </div>
              <div>
                <h2 className="font-display text-lg md:text-xl font-extrabold text-slate-900 leading-tight">
                  CA Prévisionnel
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Projection probabiliste — <span className="font-semibold">CA Net × probabilité de transformation par étape</span>
                </p>
              </div>
            </div>
            <span
              className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                previsionnel.fiabilite === "elevee"
                  ? "bg-emerald-100 text-emerald-700"
                  : previsionnel.fiabilite === "moyenne"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-slate-100 text-slate-600"
              }`}
              data-testid="cash-previsionnel-fiabilite"
            >
              Fiabilité {previsionnel.fiabilite || "faible"} · {previsionnel.n_resolus ?? 0} dossiers résolus
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MiniMetric
              label="Prévisionnel — Pipeline total"
              value={k(previsionnel.ca_previsionnel)}
              color="var(--cf-accent)"
              testid="prev-pipeline"
            />
            <MiniMetric
              label="Prévisionnel M+1"
              value={k(previsionnel.months?.[0]?.ca_previsionnel ?? 0)}
              color="var(--cf-accent)"
              testid="prev-m1"
            />
            <MiniMetric
              label="Prévisionnel M+2"
              value={k(previsionnel.months?.[1]?.ca_previsionnel ?? 0)}
              color="var(--cf-accent)"
              testid="prev-m2"
            />
          </div>
          {previsionnel.fiabilite === "faible" && (
            <div className="mt-3 text-[11px] text-slate-500 font-medium bg-white/60 border border-slate-200 rounded-lg px-3 py-2" data-testid="cash-previsionnel-warning">
              Échantillon &lt; 10 dossiers résolus sur 12 mois — projection à consulter avec prudence. Elle deviendra fiable dès que ton historique s'étoffera.
            </div>
          )}
        </section>
      )}

      {/* === 12. CA Net (rémunération) === */}
      <section className="card-base p-5 mb-4" data-testid="cash-net-remu">
        <SectionTitle icon={CurrencyEur} color="var(--cf-success)" title="CA Net (ta rémunération)" subtitle="Ce qui te reste après rétrocessions" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
          <MiniMetric label="Net signé (mois)" value={k(net.net_signed_mois)} color="var(--cf-success)" testid="net-signed" />
          <MiniMetric label="Net pipeline" value={k(net.net_pipeline)} color="var(--cf-primary)" testid="net-pipe" />
          <MiniMetric label="Net sécurisé" value={k(net.net_secured)} color="var(--cf-accent)" testid="net-secured" />
          <MiniMetric label="Net moyen/mois" value={k(net.net_moy_mois)} testid="net-moy" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-100 pt-4">
          <MiniMetric label="Trésorerie générée ce mois" value={`${net.tresorerie_generee_mois >= 0 ? "+" : ""}${k(net.tresorerie_generee_mois)}`} color={net.tresorerie_generee_mois >= 0 ? "var(--cf-success)" : "var(--cf-danger)"} testid="net-treso-mois" />
          <MiniMetric label="Projection net annuel" value={k(net.projection_net_annuel)} color="var(--cf-accent)" testid="net-proj-annu" />
          <MiniMetric label="Projection trésorerie annuelle" value={`${net.projection_tresorerie_annuelle >= 0 ? "+" : ""}${k(net.projection_tresorerie_annuelle)}`} color={net.projection_tresorerie_annuelle >= 0 ? "var(--cf-success)" : "var(--cf-danger)"} testid="net-proj-treso" />
        </div>
      </section>

      {/* === 13. Performance signés === */}
      <section className="card-base p-5 mb-4" data-testid="cash-perf-signes">
        <SectionTitle icon={Receipt} color="var(--cf-success)" title="Performance signés" subtitle="Sur la période en cours" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniMetric label="CA signé (mois)" value={k(data.performance_signes.ca_signed_month_brut)} color="var(--cf-success)" testid="ps-ca" />
          <MiniMetric label="CAPP moyen" value={k(data.performance_signes.capp_moyen)} testid="ps-capp" />
          <MiniMetric label="Dossiers signés" value={data.performance_signes.count} testid="ps-count" />
          <MiniMetric label="Délai moyen" value={`${data.performance_signes.delai_moy_jours}j`} color="var(--cf-text-3)" testid="ps-delai" />
        </div>
      </section>

      {/* === 14. Tunnel de conversion === */}
      <section className="card-base p-5 mb-4" data-testid="cash-tunnel">
        <SectionTitle icon={Funnel} color="var(--cf-primary)" title="Tunnel de conversion" subtitle="De leads à signés" />
        <TunnelStage label="Leads" value={tun.leads} pctNext={tun.rate_leads_contacts_pct} testid="tun-leads" />
        <TunnelArrow value={`${tun.rate_leads_contacts_pct.toFixed(1)}%`} />
        <TunnelStage label="Contacts / RDV" value={tun.contacts} pctNext={tun.rate_contacts_in_bank_pct} testid="tun-contacts" />
        <TunnelArrow value={`${tun.rate_contacts_in_bank_pct.toFixed(1)}%`} />
        <TunnelStage label="En banque" value={tun.in_bank} pctNext={tun.rate_in_bank_accordes_pct} testid="tun-bank" />
        <TunnelArrow value={`${tun.rate_in_bank_accordes_pct.toFixed(1)}%`} />
        <TunnelStage label="Accordés" value={tun.accordes} pctNext={tun.rate_accordes_signed_pct} testid="tun-accordes" />
        <TunnelArrow value={`${tun.rate_accordes_signed_pct.toFixed(1)}%`} />
        <TunnelStage label="Signés" value={tun.signes} final testid="tun-signes" />
      </section>

      {/* === 15. Historique total === */}
      <section className="card-base p-5 mb-6" data-testid="cash-historique">
        <SectionTitle icon={ListNumbers} color="var(--cf-neutral-text)" title="Historique total" subtitle="Tous les dossiers signés (cumul)" />
        <div className="grid grid-cols-3 gap-4">
          <MiniMetric label="CA brut total" value={k(histo.ca_brut_total)} color="var(--cf-success)" testid="hist-brut" />
          <MiniMetric label="CA net total" value={k(histo.ca_net_total)} color="var(--cf-primary)" testid="hist-net" />
          <MiniMetric label="Dossiers signés" value={histo.dossiers_signes} testid="hist-count" />
        </div>
      </section>

      <CashDrillDrawer
        open={!!drillMonth}
        month={drillMonth}
        onClose={() => setDrillMonth(null)}
      />
    </Layout>
  );
}

function TunnelStage({ label, value, pctNext, final, testid }) {
  return (
    <div className="bg-[color:var(--cf-bg-2)] rounded-xl px-4 py-3 flex items-center justify-between border border-slate-100" data-testid={testid}>
      <div className="text-[14px] font-bold text-slate-700">{label}</div>
      <div className="flex items-center gap-3">
        <div className="text-[20px] font-extrabold tabular-nums">{value}</div>
        {!final && pctNext != null && (
          <span className="px-2 py-0.5 rounded-md bg-[color:var(--cf-primary-soft)] text-[color:var(--cf-primary-hover)] text-[11px] font-bold tabular-nums">
            {pctNext.toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

function TunnelArrow({ value }) {
  return (
    <div className="flex items-center justify-center my-1">
      <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-slate-300" />
    </div>
  );
}
